import { useMsal } from '@azure/msal-react';
import { useCreateMeetingMutation, useCreateRecurringSeriesMutation } from '@consigli/hooks';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { RecurrenceEndType, RecurrencePattern } from '@/util/date';

export interface CreateMeetingFormData {
  title: string;
  date: string;
  time: string;
  duration: number;
  attendees: string[];
  description: string;
  location?: string;
  isRecurring?: boolean;
  recurrencePattern?: RecurrencePattern;
  recurrenceInterval?: number;
  daysOfWeek?: string[];
  recurrenceEndType?: RecurrenceEndType;
  recurrenceEndDate?: string;
  numberOfOccurrences?: number;
}

interface UseMeetingCreationParams {
  projectId: number;
  /** Called after a meeting/series is created successfully (e.g. to close the modal). */
  onSuccess?: () => void;
}

/**
 * Creates a meeting (or recurring series) in Outlook via the backend, acquiring
 * an MS Graph token first. Owns the create-in-flight `loading` and `error` state.
 */
export const useMeetingCreation = ({ projectId, onSuccess }: UseMeetingCreationParams) => {
  const { t } = useTranslation();
  const { accounts, instance } = useMsal();
  const [createMeetingMutation] = useCreateMeetingMutation();
  const [createRecurringSeriesMutation] = useCreateRecurringSeriesMutation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createMeeting = async (formData: CreateMeetingFormData) => {
    setLoading(true);
    setError(null);

    try {
      const account = accounts[0];
      if (!account) {
        throw new Error('No authenticated account found');
      }

      const tokenRequest = {
        scopes: ['Calendars.ReadWrite', 'OnlineMeetings.ReadWrite', 'User.Read'],
        account,
      };

      const tokenResponse = await instance.acquireTokenSilent(tokenRequest);

      // Create meeting from form data - preserve user's local timezone
      // Note: JavaScript Date constructor interprets "2025-07-05T20:07" as LOCAL time
      // When we call .toISOString(), it converts to UTC (e.g., India 20:07 → UTC 14:37)
      // This is correct behavior - meetings should be stored in UTC and displayed in local time
      const startDateTime = new Date(`${formData.date}T${formData.time}`);
      const endDateTime = new Date(startDateTime.getTime() + formData.duration * 60 * 1000);

      // Get user's timezone for backend reference
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const location = formData.location?.trim() || undefined;

      // Handle recurring meetings differently
      if (formData.isRecurring) {
        // Calculate end_time from time + duration
        const [hours, minutes] = formData.time.split(':').map(Number);
        const startTime = new Date();
        startTime.setHours(hours, minutes, 0, 0);
        const endTime = new Date(startTime.getTime() + formData.duration * 60000);
        const endTimeString = `${endTime.getHours().toString().padStart(2, '0')}:${endTime.getMinutes().toString().padStart(2, '0')}`;

        // Map frontend recurrence_end_type to backend format
        const rangeType =
          formData.recurrenceEndType === RecurrenceEndType.ByDate ? 'endDate' : 'numbered';

        // Map pattern type to backend format
        const patternType =
          formData.recurrencePattern === RecurrencePattern.Monthly
            ? 'absoluteMonthly'
            : formData.recurrencePattern!;

        // Get day of month for monthly recurrence
        const dayOfMonth =
          formData.recurrencePattern === RecurrencePattern.Monthly
            ? new Date(formData.date).getDate()
            : undefined;

        const recurringData = {
          title: formData.title,
          description: formData.description,
          location,
          start_time: formData.time,
          end_time: endTimeString,
          timezone: userTimezone,
          attendees: formData.attendees,
          recurrence_pattern: {
            type: patternType,
            interval: formData.recurrenceInterval || 1,
            days_of_week:
              formData.recurrencePattern === RecurrencePattern.Weekly
                ? formData.daysOfWeek
                : undefined,
            day_of_month: dayOfMonth,
          },
          recurrence_range: {
            type: rangeType,
            start_date: formData.date, // YYYY-MM-DD
            end_date:
              formData.recurrenceEndType === RecurrenceEndType.ByDate
                ? formData.recurrenceEndDate
                : undefined,
            number_of_occurrences:
              formData.recurrenceEndType === RecurrenceEndType.AfterOccurrences
                ? formData.numberOfOccurrences
                : undefined,
          },
        };

        await createRecurringSeriesMutation({
          projectId,
          msAccessToken: tokenResponse.accessToken,
          userEmail: account.username || '',
          userId: account.localAccountId || '',
          body: recurringData,
        }).unwrap();

        toast.success(t('meeting-secretary.meetings.recurring-series-created-successfully'));
      } else {
        // Regular meeting creation
        await createMeetingMutation({
          projectId,
          msAccessToken: tokenResponse.accessToken,
          userEmail: account.username || '',
          userId: account.localAccountId || '',
          body: {
            title: formData.title,
            startDateTime: startDateTime.toISOString(),
            endDateTime: endDateTime.toISOString(),
            organizerId: account.localAccountId || '',
            attendees: formData.attendees,
            description: formData.description,
            location,
            timezone: userTimezone,
            projectId,
          },
        }).unwrap();

        toast.success(t('meeting-secretary.meetings.meeting-created-successfully'));
      }

      onSuccess?.();
    } catch (error) {
      console.error('Error creating meeting:', error);
      setError(
        error instanceof Error ? error.message : 'Failed to create meeting. Please try again.',
      );
      toast.error(error instanceof Error ? error.message : 'Failed to create meeting');
    } finally {
      setLoading(false);
    }
  };

  return { createMeeting, loading, error };
};
